import { z } from 'zod';
import { FieldValue, coerceLegacyFieldValue } from './fieldPatterns.schemas';

// ============================================================================
// COMPANY & SITE SCHEMAS
// ============================================================================

export const PersonRecord = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  role: z.string().optional(),
  scope: z.enum(['company', 'site']).default('company'),
  siteId: z.string().min(1).optional(),
  reportRecipient: z.boolean().default(false),
  alarmContact: z.boolean().default(false),
  fireTraining: z.boolean().default(false),
  firstAidTraining: z.boolean().default(false),
  licensed: z.boolean().default(false),
  licenseExpiry: z.string().optional(),
  licenseLastUpdate: z.string().optional(),
  reperibile: z.boolean().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().optional(),
});

export const SiteComponent = z.object({
  id: z.string(),
  type: z.string(),
  quantity: z.number().int().min(0).default(0),
  notes: z.string().optional(),
});

// For synchronous usage (form validation), use a simple string type
// The actual validation happens at the service layer
export const RefrigerationUnitComponentType = z.string();

export const RefrigerationUnitComponent = z.object({
  id: z.string(),
  type: RefrigerationUnitComponentType.optional(),
  customLabel: z.string().optional(),
  quantity: z.number().int().min(0).optional(),
  capacityKw: z.number().nonnegative().optional(),
  location: z.string().optional(),
  notes: z.string().optional(),
  btGreaseBand: z.boolean().optional(),
});

export const RefrigerationUnitSafetyThreshold = z.object({
  id: z.string(),
  thresholdType: z.enum(['tossicita', 'esplosivita']).default('tossicita'),
  unit: z.enum(['ppm', 'percent_lel']),
  value: z.number().nonnegative(),
  actions: z.array(z.string().min(1)).min(1),
});

const RefrigerationUnitVentilation = z
  .object({
    type: z.string().optional(),
    flowRateM3h: z.number().nonnegative().optional(),
    notes: z.string().optional(),
    naturalPresent: z.boolean().optional(),
    apertureHighM2: z.number().nonnegative().optional(),
    apertureLowM2: z.number().nonnegative().optional(),
    natural: z
      .object({
        highOpeningsAreaM2: z.number().nonnegative().optional(),
        lowOpeningsAreaM2: z.number().nonnegative().optional(),
        notes: z.string().optional(),
      })
      .optional(),
    extractors: z
      .object({
        units: z.number().int().min(0).optional(),
        airflowM3h: z.number().nonnegative().optional(),
        atex: z.boolean().optional(),
        logic: z.string().optional(),
        mode: z.string().optional(),
      })
      .optional(),
    extractorsCount: z.number().int().min(0).optional(),
    extractorsFlowM3h: z.number().nonnegative().optional(),
    extractorsOperation: z.string().optional(),
    extractorsAtex: z.boolean().optional(),
  })
  .optional();

const RefrigerationUnitScrubber = z
  .object({
    present: z.boolean().default(false),
    notes: z.string().optional(),
    airFlowM3h: z.number().nonnegative().optional(),
    waterFlowM3h: z.number().nonnegative().optional(),
    waterFlowSpec: z.string().optional(),
    contactTimeSeconds: z.number().nonnegative().optional(),
    waterSupply: z.string().optional(),
    wasteCollection: z.string().optional(),
    atex: z.boolean().optional(),
  })
  .default({ present: false });

const RefrigerationUnitAbatementSystem = z
  .object({
    present: z.boolean().default(false),
    controlMode: z.enum(['manuale', 'automatico']).optional(),
    trim: z.boolean().optional(),
    limitSwitches: z.boolean().optional(),
    type: z.string().optional(),
    supply: z.string().optional(),
    disposal: z.string().optional(),
    waterFlowM3h: z.number().nonnegative().optional(),
  })
  .default({ present: false });

const RefrigerationUnitPowerBackup = z
  .object({
    preferredLine: z.boolean().optional(),
    generator: z.boolean().optional(),
    ups: z.boolean().optional(),
    notes: z.string().optional(),
  })
  .optional();

const RefrigerationUnitEmergencySystems = z
  .object({
    dialerPresent: z.boolean().default(false),
    guardServicePresent: z.boolean().default(false),
    generatorPresent: z.boolean().default(false),
    notes: z.string().optional(),
    eeTripButtons: z.boolean().optional(),
    eeTripDetector: z.boolean().optional(),
    // New safety systems fields
    elettroserraturaPresent: z.boolean().optional(),
    emergencyLightingPresent: z.boolean().optional(),
    emergencyLightingAtex: z.boolean().optional(),
    sgancioAutomaticoPresent: z.boolean().optional(),
    sogliaSgancioAutomatico: z.string().optional(),
    pulsanteSgancioPresent: z.boolean().optional(),
  })
  .default({
    dialerPresent: false,
    guardServicePresent: false,
    generatorPresent: false,
  });

const RefrigerationUnitDetectionMaintenance = z
  .object({
    name: z.string().optional(),
    notes: z.string().optional(),
  })
  .optional();

const RefrigerationUnitValves = z
  .object({
    reliefSetPressuresBar: z.array(z.number().nonnegative()).optional(),
    reliefPeriodicityYears: z.number().int().min(0).optional(),
    reliefNotes: z.string().optional(),
    isolationPresent: z.boolean().optional(),
    isolationDiverter: z.boolean().optional(),
    isolationNotes: z.string().optional(),
  })
  .optional();

const RefrigerationUnitVsRelief = z
  .object({
    collectorsCount: z.number().int().min(0).optional(),
    sizing: z.string().optional(),
  })
  .optional();

const RefrigerationUnitAbatementTank = z
  .object({
    present: z.boolean().optional(),
    volumeMc: z.number().nonnegative().optional(),
    minWaterMc: z.number().nonnegative().optional(),
    minFreeMc: z.number().nonnegative().optional(),
    location: z.enum(['interrata', 'fuori_terra']).optional(),
    signage: z.enum(['segnalata', 'non_segnalata']).optional(),
    accessibility: z.enum(['agevole', 'difficoltosa']).optional(),
    material: z.enum(['ca', 'acciaio_zincato', 'vetroresina', 'polietilene']).optional(),
    dimensions: z.string().optional(),
    orientation: z.enum(['orizzontale', 'verticale']).optional(),
    levelInstrumentation: z
      .object({
        present: z.boolean().optional(),
        type: z.enum(['elettronico', 'meccanico', 'visivo']).optional(),
      })
      .optional(),
    continuousPhMonitoring: z.boolean().optional(),
    emergencySupply: z
      .object({
        preferredLine: z.boolean().optional(),
        generator: z.boolean().optional(),
        ups: z.boolean().optional(),
      })
      .optional(),
  })
  .optional();

export const RefrigerationUnitSafety = z
  .object({
    detectors: z
      .object({
        toxicCount: z.number().int().min(0).default(0),
        explosiveCount: z.number().int().min(0).default(0),
        positions: z.array(z.string()).default([]),
        thresholds: z.array(RefrigerationUnitSafetyThreshold).default([]),
        calibrationNotes: z.string().optional(),
        ceilingCount: z.number().int().min(0).optional(),
        humanHeightCount: z.number().int().min(0).optional(),
      })
      .default({
        toxicCount: 0,
        explosiveCount: 0,
        positions: [],
        thresholds: [],
      }),
    ventilation: RefrigerationUnitVentilation,
    scrubber: RefrigerationUnitScrubber,
    abatementSystem: RefrigerationUnitAbatementSystem,
    powerBackup: RefrigerationUnitPowerBackup,
    emergencySystems: RefrigerationUnitEmergencySystems,
    valves: RefrigerationUnitValves,
    detectionMaintenance: RefrigerationUnitDetectionMaintenance,
    vsRelief: RefrigerationUnitVsRelief,
    abatementTank: RefrigerationUnitAbatementTank,
    vvfDocs: z.enum(['prevista', 'non_prevista']).optional(),
    vvfDocsLocation: z.string().optional(),
  })
  .default({
    detectors: {
      toxicCount: 0,
      explosiveCount: 0,
      positions: [],
      thresholds: [],
    },
    scrubber: { present: false },
    emergencySystems: {
      dialerPresent: false,
      guardServicePresent: false,
      generatorPresent: false,
    },
    abatementSystem: { present: false },
  });

export const RefrigerationUnitTechnical = z
  .object({
    components: z.array(RefrigerationUnitComponent).default([]),
    maintenanceContractor: z
      .object({
        name: z.string().optional(),
        notes: z.string().optional(),
      })
      .optional(),
    safety: RefrigerationUnitSafety,
    notes: z.string().optional(),
  })
  .default({
    components: [],
    safety: {
      detectors: {
        toxicCount: 0,
        explosiveCount: 0,
        positions: [],
        thresholds: [],
      },
      scrubber: { present: false },
      emergencySystems: {
        dialerPresent: false,
        guardServicePresent: false,
        generatorPresent: false,
      },
      abatementSystem: { present: false },
    },
  });

export const RefrigerationUnit = z.object({
  id: z.string(),
  refrigerationUnitId: z.string(),
  name: z.string(),
  location: z
    .preprocess((value) => coerceLegacyFieldValue(value, 'open_enum'), FieldValue)
    .optional(),
  ammoniaKg: z.number().nonnegative().optional(),
  toxicGasType: z
    .preprocess((value) => coerceLegacyFieldValue(value, 'closed_enum'), FieldValue)
    .optional(),
  impiantoType: z
    .preprocess((value) => coerceLegacyFieldValue(value, 'open_enum'), FieldValue)
    .optional(),
  plannedPeriod: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
  plannedDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  plannedSource: z.string().optional(),
  plannedUpdatedAt: z.string().optional(),
  technical: RefrigerationUnitTechnical,
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().optional(),
});

export const Company = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional(),
  vat: z.string().optional(),
  sedeLegale: z.string().optional(),
  sedeLegaleAddress: z.string().optional(),
  sedeLegaleCap: z.string().optional(),
  sedeLegaleCity: z.string().optional(),
  sedeLegaleProvince: z.string().optional(),
  notes: z.string().optional(),
  logoUrl: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  personnel: z.array(PersonRecord).default([]),
});

export const Site = z.object({
  id: z.string(),
  companyId: z.string(),
  name: z.string(),
  sequenceNumber: z.number().int().positive(),
  contactEmail: z.string().optional(),
  contactPhone: z.string().optional(),
  btGreaseBandPresent: z.boolean().optional(),
  address: z.string().optional(),
  siteLocation: z.string().optional(),
  siteType: z.string().optional(),
  frequency: z.string().optional(),
  gasType: z.string().optional(),
  toxicGas: z.string().optional(),
  cpiPresent: z.boolean().optional(),
  cpiProtocolNumber: z.string().optional(),
  cpiLastRenewalDate: z.string().optional(),
  cpiExpiryDate: z.string().optional(),
  ammoniaActivityCodes: z.boolean().optional(),
  authorizationProtocolNumber: z.string().optional(),
  authorizationIssueDate: z.string().optional(),
  authorizationExpiryDate: z.string().optional(),
  authorizedNh3Kg: z.number().nonnegative().optional(),
  officialGazettePublished: z.boolean().optional(),
  officialGazetteDate: z.string().optional(),
  authorizationPrescriptions: z.string().optional(),
  periodicityControlPeriod: z.string().optional(),
  periodicityPreviousVisit: z.string().optional(),
  periodicityNextVisit: z.string().optional(),
  periodicityNextVisitAt: z.string().optional(),
  maintenanceNotes: z.string().optional(),
  sedeOperativa: z.string().optional(),
  sedeOperativaAddress: z.string().optional(),
  sedeOperativaCap: z.string().optional(),
  sedeOperativaCity: z.string().optional(),
  sedeOperativaProvince: z.string().optional(),
  periodicity: z.enum(['1', '3', '4', '6']).optional(), // months between inspections
  contractActivationDate: z.string().optional(), // ISO date string (YYYY-MM-01 from month/year input)
  // Computed fields (derived from activation date + periodicity)
  nextInspectionWindowStart: z.string().optional(),
  nextInspectionWindowEnd: z.string().optional(),
  // Keep essential fields for existing functionality
  refrigerationUnitsCount: z.number().int().min(0).default(0),
  siteComponents: z.array(SiteComponent).default([]),
  refrigerationUnits: z.array(RefrigerationUnit).default([]),
  notes: z.string().optional(),
  reperibilePhone: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Export types
export type PersonRecord = z.infer<typeof PersonRecord>;
export type SiteComponent = z.infer<typeof SiteComponent>;
export type RefrigerationUnitComponent = z.infer<typeof RefrigerationUnitComponent>;
export type RefrigerationUnitSafetyThreshold = z.infer<typeof RefrigerationUnitSafetyThreshold>;
export type RefrigerationUnitSafety = z.infer<typeof RefrigerationUnitSafety>;
export type RefrigerationUnit = z.infer<typeof RefrigerationUnit>;
export type Company = z.infer<typeof Company>;
export type Site = z.infer<typeof Site>;
